import React from 'react';
import {
  Box,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import BeachAccessRoundedIcon from '@mui/icons-material/BeachAccessRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import PauseCircleRoundedIcon from '@mui/icons-material/PauseCircleRounded';
import { EmptyState } from '../ui';

function getAnnualAllowance(contractType) {
  if (contractType === '30 Days Yearly') return 30;
  if (contractType === '24 Days Yearly') return 24;
  if (contractType === '21 Days Yearly') return 21;
  if (contractType === '20 Days Yearly') return 20;
  return null;
}

function MetricTile({ label, value, icon, tone }) {
  return (
    <Box
      sx={{
        p: 1.1,
        borderRadius: 2.2,
        border: '1px solid',
        borderColor: alpha(tone, 0.16),
        backgroundColor: alpha(tone, 0.08),
      }}
    >
      <Stack direction="row" spacing={0.8} alignItems="center">
        <Box sx={{ color: tone, display: 'flex', alignItems: 'center' }}>{icon}</Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

export default function AnnualLeaveSpotlight({
  annualBalance,
  contractType,
}) {
  const theme = useTheme();
  const allowance = getAnnualAllowance(contractType);

  if (!allowance) {
    return (
      <EmptyState
        compact
        title="No allowance"
        description="Ask HR to set your annual contract allowance."
      />
    );
  }

  if (!annualBalance) {
    return (
      <EmptyState
        compact
        title="No annual balance"
        description="No annual leave balance is available for this employee."
      />
    );
  }

  const current = Number(annualBalance.current_balance || 0);
  const onHold = Number(annualBalance.on_hold ?? annualBalance.pending_requests ?? 0);
  const taken = Number(annualBalance.days_taken || 0);
  const projected = Number(annualBalance.projected_balance || current);
  const usedPct = Math.max(0, Math.min(100, Math.round((taken / allowance) * 100)));
  const availablePct = Math.max(0, Math.min(100, Math.round((current / allowance) * 100)));

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        p: 1.65,
        border: '1px solid',
        borderColor: alpha(theme.palette.secondary.main, 0.18),
        background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.light, 0.24)} 0%, ${alpha(theme.palette.primary.light, 0.18)} 42%, ${alpha(theme.palette.success.light, 0.18)} 100%)`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          width: 140,
          height: 140,
          borderRadius: '50%',
          top: -64,
          right: -26,
          backgroundColor: alpha(theme.palette.secondary.main, 0.12),
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: 108,
          height: 108,
          borderRadius: '50%',
          bottom: -48,
          left: -18,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
        }}
      />

      <Grid container spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack spacing={1.1}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.7 }}>
              <Chip
                icon={<BeachAccessRoundedIcon sx={{ fontSize: 16 }} />}
                size="small"
                label="Annual Leave Spotlight"
                sx={{
                  fontWeight: 900,
                  backgroundColor: alpha(theme.palette.background.paper, 0.56),
                }}
              />
              <Chip
                size="small"
                label={`${allowance} d allowance`}
                sx={{
                  fontWeight: 800,
                  color: theme.palette.secondary.dark,
                  backgroundColor: alpha(theme.palette.secondary.main, 0.12),
                }}
              />
            </Stack>

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.8, color: 'text.secondary' }}>
                YEARLY TIME-OFF SNAPSHOT
              </Typography>
              <Typography variant="h5" sx={{ mt: 0.35, fontWeight: 900, lineHeight: 1.1 }}>
                {current.toFixed(1)} days ready to use
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.55 }}>
                Reserved time and projected accrual are surfaced together so your year-end balance is easier to read at a glance.
              </Typography>
            </Box>

            <Grid container spacing={1.1}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <MetricTile
                  label="Reserved"
                  value={`${onHold.toFixed(1)} d`}
                  icon={<PauseCircleRoundedIcon sx={{ fontSize: 18 }} />}
                  tone={theme.palette.warning.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <MetricTile
                  label="Projected Year-End"
                  value={`${projected.toFixed(1)} d`}
                  icon={<TrendingUpRoundedIcon sx={{ fontSize: 18 }} />}
                  tone={theme.palette.success.main}
                />
              </Grid>
            </Grid>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Stack spacing={1.1} alignItems="center">
            <Box
              sx={{
                position: 'relative',
                width: 172,
                height: 172,
                borderRadius: '50%',
                p: '14px',
                background: `conic-gradient(${theme.palette.secondary.main} 0deg ${usedPct * 3.6}deg, ${alpha(theme.palette.secondary.main, 0.16)} ${usedPct * 3.6}deg 360deg)`,
                boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.08)}`,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  backgroundColor: alpha(theme.palette.background.paper, 0.88),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.9, color: 'text.secondary' }}>
                  USED
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 900, lineHeight: 1 }}>
                  {usedPct}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {taken.toFixed(1)} d taken
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.7, justifyContent: 'center' }}>
              <Chip
                size="small"
                variant="outlined"
                label={`${availablePct}% available now`}
                sx={{ fontWeight: 800 }}
              />
              <Chip
                size="small"
                label={`${taken.toFixed(1)} d completed`}
                sx={{
                  fontWeight: 800,
                  color: theme.palette.success.dark,
                  backgroundColor: alpha(theme.palette.success.main, 0.12),
                }}
              />
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
